import { ACTION_MESSAGES, AI_LABEL_CLASS, NO_CHOICES_SELECTED, NO_INPUT_DATA } from "./constants";
import Html from 'slate-html-serializer';
import textDeserializationRules from './deserializationRules';
import exportService from 'services/ExportService';
import activityService from 'services/ActivityService';
import { status } from 'Workspace/Publications/utils/constants';
import {
  TextElement,
  PlaceholderElement,
} from "@pearsontechnology/aero-stage-presentation-framework/dist/rce/model";

export const generateStatement = (selectedChoices) => {
  const statements = Object.entries(selectedChoices)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
  return statements || NO_CHOICES_SELECTED;
};

export const generateFormattedStatement = (data = {}) => {
  if (!data || Object.keys(data).length === 0) {
    return NO_INPUT_DATA; 
  }

  return Object.entries(data)
    .map(([key, value]) => {
      const formattedKey = key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (char) => char.toUpperCase());
      return `${formattedKey}: **${value}**`;
    })
    .join(', ');
};

export const getActionMessage = (actionType, globalState = {}) => {
  switch (actionType) {
    case ACTION_MESSAGES.REQ_CHOICE_SELECTION:
      return `${ACTION_MESSAGES.RES_CHOICE_SELECTION} ${generateStatement(globalState?.selectedChoices)}`;
    case ACTION_MESSAGES.REQ_VIEW_PROMPTS:
      return ACTION_MESSAGES.RES_VIEW_PROMPT;
    case ACTION_MESSAGES.REQ_PROMPTS:
      return globalState?.promptMessage;
    case ACTION_MESSAGES.REQ_GENERATE_SUMMARY:
      return ACTION_MESSAGES.RES_GENERATE_SUMMARY;
    case ACTION_MESSAGES.REQ_RES_RETRY:
      return ACTION_MESSAGES.REQ_RES_RETRY;
    case ACTION_MESSAGES.REQ_EDIT_PROMPT:
      return ACTION_MESSAGES.RES_EDIT_PROMPT;
    case ACTION_MESSAGES.REQ_RES_CONTINUE:
      return ACTION_MESSAGES.REQ_RES_CONTINUE;
    case ACTION_MESSAGES.REQ_SUMMARY_MESSAGE:
      return ACTION_MESSAGES.RES_SUMMARY_MESSAGE;
    case ACTION_MESSAGES.REQ_CREATED_SUMMARY:
      return globalState?.summaryContent;
    case ACTION_MESSAGES.REQ_RES_INSERT:
      return ACTION_MESSAGES.REQ_RES_INSERT;
    case ACTION_MESSAGES.REQ_INSERTED:
      return `${ACTION_MESSAGES.RES_INSERTED} ${globalState.chapterName}`;
    case ACTION_MESSAGES.REQ_MODELCHANGE:
      return ACTION_MESSAGES.RES_MODAL_CHANGE(globalState);
    case ACTION_MESSAGES.REQ_FIELD_INPUTS:
      return `${ACTION_MESSAGES.RES_FIELD_INPUTS} ${generateFormattedStatement(globalState?.caseStudyFormData)}`;
    case ACTION_MESSAGES.REQ_RES_GENERATE:
      return ACTION_MESSAGES.REQ_RES_GENERATE;
    case ACTION_MESSAGES.REQ_CASE_STUDY_MESSAGE:
      return ACTION_MESSAGES.RES_CASE_STUDY_MESSAGE;
    case ACTION_MESSAGES.REQ_CREATED_CASE_STUDY:
      return globalState?.caseStudyContent;
    case ACTION_MESSAGES.REQ_GENERATE_CASE_STUDY:
      return ACTION_MESSAGES.RES_GENERATE_CASE_STUDY;
    case ACTION_MESSAGES.REQ_CASE_STUDY_INSERTED:
      return `${ACTION_MESSAGES.RES_CASE_STUDY_INSERTED} ${globalState.chapterName}`;
    case ACTION_MESSAGES.REQ_TRANSLATE_MESSAGE:
      return ACTION_MESSAGES.RES_TRANSLATE_MESSAGE;
    default:
      return ACTION_MESSAGES.RES_UNKNOWN_ACTION;
  }
};

export const userActions = async (actionType, userType, globalState, setGlobalState) => {
  const actionMsg = getActionMessage(actionType, globalState);
  setGlobalState((prev) => ({
    ...prev,
    userActions: [
      ...prev.userActions
      ,
      {
        type: userType,
        content: actionMsg,
      },
    ],
  }));
};

export const extractContent = (summaryContent) => {
  let content = summaryContent.trim();
  
  // Remove starting ```html with any whitespace and trailing ```
  content = content.replace(/^```html\s*/i, "").replace(/\s*```$/, "");
  
  const bodyMatch = content.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  content = bodyMatch ? bodyMatch[1].trim() : content;
  
  // Remove spaces between \n and <p> for markdown-to-jsx compatibility
  content = content
    .replace(/\n\s*/g, "")
    .replace(/>\s+</g, "><")
    .replace(/^"|"$/g, "");


  
  return content;
};

export const createContent = (summary) => {
  const regex = /<(h[1-6]|p)>([\s\S]*?)<\/\1>|<(ul|ol)>([\s\S]*?)<\/\3>/g;
  const elements = [...summary.matchAll(regex)].map((match) => match[0]);

  const htmlSerializer = new Html({ rules: textDeserializationRules });

  const elementsArray = elements.map((el) => {
    const deserialize = htmlSerializer.deserialize(el, { toJSON: true });
    return deserialize ? new TextElement(deserialize) : new PlaceholderElement();
  });

  elementsArray.forEach((element) => {
    element.config = {
      ...element.config,
      cssClass: AI_LABEL_CLASS,
    };
  });

  return elementsArray;
};

export const extractTitleFromHTML = (htmlString) => {

  if (typeof htmlString !== 'string') return '';

  const titleMatch = htmlString.match(/<title>(.*?)<\/title>/i);
  if (titleMatch && titleMatch[1]) {
    return titleMatch[1].trim();
  }


  const headingMatch = htmlString.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
  if (headingMatch && headingMatch[1]) {
    return headingMatch[1].replace(/<[^>]*>/g, '').trim();
  }

  const pMatch = htmlString.match(/<p[^>]*>(.*?)<\/p>/i);
  if (pMatch && pMatch[1]) {
    return pMatch[1].replace(/<[^>]*>/g, '').trim();
  }

  return '';
};

export const exportAndGetAmbrosiaUrl = async (activityId, threeLevelToc, elementType) => {
  const { exportId } = await exportService.exportForPreview(activityId, elementType, threeLevelToc);
  return new Promise((resolve, reject) => {
    let timeoutId;
    
    const cleanup = () => {
      exportService.unSubscribeToExport(exportId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const handleExportStatus = ({ response }) => {
      if (!response) return;
      
      const { progress, ambrosiaUrl } = response;
      if (progress && [status.COMPLETED, status.FAILED].includes(status[progress])) {
        cleanup();
        
        if (status[progress] === status.COMPLETED && ambrosiaUrl) {
          resolve(ambrosiaUrl);
        } else {
          reject(new Error('Export failed or no ambrosiaUrl returned'));
        }
      }
    };

    // Set up subscription and timeout
    exportService.subscribeToExport(exportId, handleExportStatus);
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('Export timeout'));
    }, 60000);
  });
};


export const extractPathname = (pathname) => {
  if (!pathname || !pathname.includes('/screen/')) {
    return;
  }
  
  const screenMatch = pathname.match(/\/screen\/([a-z0-9-]+)/);
  if (!screenMatch || !screenMatch[1]) {
    return;
  }
  return screenMatch[1];
};

export const getActivityTitle = async (activityId) => {
  try {
    const activity = await activityService.getActivity(activityId);
    return activity?.config?.title || '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch activity title:', error);
    return '';
  }
};

/**
 * Parses error from RTM response format.
 * RTM errors can come in the format: { "data": "{\"error\":\"Request failed with status code 504\"}" }
 * or { "error": { "message": "...", "code": "..." } }
 * @param {Object} response
 * @returns {Object|null}
 */
export const parseRTMError = (response) => {
  if (!response) return null;

  // Check for direct error format: { error: { message, code } }
  if (response.error) {
    return {
      message: response.error.message || response.error,
      code: response.error.code,
    };
  }

  // Check for error in data field (JSON string format)
  if (response.data && typeof response.data === 'string') {
    try {
      const parsedData = JSON.parse(response.data);
      if (parsedData && typeof parsedData === 'object' && parsedData.error) {
        return {
          message: typeof parsedData.error === 'string' 
            ? parsedData.error 
            : parsedData.error.message || parsedData.error,
          code: parsedData.error.code,
        };
      }
    } catch (e) {
      // If parsing fails, data might not be JSON, continue to check other formats
    }
  }

  // Check for error in parsed data object
  if (response.data && typeof response.data === 'object' && response.data.error) {
    return {
      message: typeof response.data.error === 'string'
        ? response.data.error
        : response.data.error.message || response.data.error,
      code: response.data.error.code,
    };
  }

  // Check for direct message field
  if (response.message) {
    return {
      message: response.message,
      code: response.code,
    };
  }

  return null;
};